import { assert, trackWorkerExceptions } from '@fe/core';
import { Actor, ActorMessage } from './actor/actor';
import { MessageType, RequestResponseMessageMap } from './actor/actor-messages';

// Long story short: if we just import the worker here import worker from '...?worker' vite will bundle the worker js file as an asset. This means that the consuming package (common-map) will be unable to perform any url replacement inside the worker.js file. This is important, because the worker contains a reference to external zstd.wasm file. The path will be incorrect in embedded and won't be easy to change
// Therefore, worker is treated as a second entry point and is built into its own bundle. So in common-map and fe-web we need to import it like `import Worker from '@fe/weather/worker?worker'` this and call `setWorkerFactory(() => new Worker())`; No the worker is part of the dependency graph of vite and its contents are subject to URL rewrites.
// I lost nearly 2 days over this...
let workerFactory: (() => Worker) | null = null;

export function setWorkerFactory(factory: NonNullable<typeof workerFactory>) {
    workerFactory = factory;
}

export class Dispatcher {
    private readonly actor: Actor;
    private isInitialized = false;
    private initializePromise: Promise<void>;
    private resolveIsInitialized!: () => void;

    constructor() {
        assert(workerFactory);
        this.initializePromise = new Promise((resolve) => {
            this.resolveIsInitialized = resolve;
        });

        const worker = workerFactory();
        trackWorkerExceptions(worker);
        this.actor = new Actor(worker);
        this.actor.registerMessageHandler(MessageType.initialized, async () => {
            this.isInitialized = true;
            this.resolveIsInitialized();
        });
    }

    dispatch<T extends MessageType>(message: ActorMessage<T>): Promise<RequestResponseMessageMap[T][1]> {
        if (this.isInitialized) {
            return this.actor.sendAsync(message);
        }

        // Wait for the worker to initialize
        return new Promise<RequestResponseMessageMap[T][1]>((resolve, reject) => {
            this.initializePromise.then(() => this.actor.sendAsync(message).then(resolve).catch(reject));
        });
    }

    remove() {
        this.actor.remove();
    }
}

let globalDispatcher: Dispatcher | null = null;

export function getGlobalDispatcher() {
    if (typeof WorkerGlobalScope !== 'undefined' && typeof self !== 'undefined' && self instanceof WorkerGlobalScope) {
        throw new Error('Dispatcher is only available in non-worker context');
    }
    return (globalDispatcher ??= new Dispatcher());
}

export function removeGlobalDispatcher() {
    globalDispatcher?.remove();
    globalDispatcher = null;
}